import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { aboutContent, businessInfo } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const imageOffRef = useRef(null);
  const imageOnRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Content animation
      gsap.fromTo(
        '.about-content',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Image animation
      gsap.fromTo(
        '.about-image-container',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Glow line animation on scroll
      gsap.fromTo(
        '.about-glow-line',
        { scaleY: 0 },
        {
          scaleY: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Scroll-triggered image transition - lights turn on
      gsap.to(imageOnRef.current, {
        opacity: 1,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 38%',
          end: 'top 8%',
          scrub: 1,
        },
      });

      // Fade out the "lights off" image
      gsap.to(imageOffRef.current, {
        opacity: 0,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 38%',
          end: 'top 8%',
          scrub: 1,
        },
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const highlights = [
    "Owners personally on every project",
    "30+ years combined expertise",
    "End-to-end electrical & security solutions",
    "Licensed and fully insured"
  ];

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-securelec-light">
      {/* Background Elements */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent about-glow-line origin-top" />

      <div className="flex flex-col lg:flex-row min-h-[700px]">
        {/* Content Column - 45% on large screens */}
        <div ref={contentRef} className="w-full lg:w-[45%] py-16 lg:py-24 px-6 lg:pl-[5%] lg:pr-12 flex items-center">
          <div className="max-w-xl">
            <span className="about-content inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              About Us
            </span>
            <h2 className="about-content text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              {aboutContent.title}
            </h2>
            <div className="about-content w-16 h-1 bg-securelec-red mb-8" />
            <p className="about-content text-lg text-securelec-gray/80 mb-6 leading-relaxed">
              {aboutContent.description}
            </p>
            <p className="about-content text-securelec-gray/70 mb-8 leading-relaxed">
              {aboutContent.extendedDescription}
            </p>

            {/* Highlights */}
            <div className="about-content grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-securelec-red flex-shrink-0" />
                  <span className="text-securelec-gray text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="about-content flex flex-wrap gap-4 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
                <span className="text-securelec-gray/50 text-xs block">Electrical License</span>
                <span className="text-securelec-dark font-semibold">{businessInfo.licenses.electrical}</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
                <span className="text-securelec-gray/50 text-xs block">Security License</span>
                <span className="text-securelec-dark font-semibold">{businessInfo.licenses.security}</span>
              </div>
            </div>

            <a
              href="/about"
              className="about-content inline-flex items-center gap-2 text-securelec-red hover:text-securelec-dark transition-colors duration-300 font-semibold group"
            >
              Learn More About Us
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
            </a>
          </div>
        </div>

        {/* Image Column - 55% on large screens with angled edge */}
        <div className="about-image-container w-full lg:w-[55%] relative min-h-[400px] lg:min-h-full order-first lg:order-last">
          {/* Angled clip container - only on large screens */}
          <div 
            className="absolute inset-0 lg:[clip-path:polygon(10%_0,100%_0,100%_100%,0%_100%)]"
          >
            {/* Lights Off Image (Initial) */}
            <img
              ref={imageOffRef}
              src="https://customer-assets.emergentagent.com/job_power-pulse-18/artifacts/a9j54sqq_Before.jpg"
              alt="Home exterior before lighting installation Bayside Melbourne"
              className="w-full h-full object-cover absolute inset-0"
            />
            {/* Lights On Image (Revealed on Scroll) */}
            <img
              ref={imageOnRef}
              src="https://customer-assets.emergentagent.com/job_power-pulse-18/artifacts/ldy85806_After.jpg"
              alt="Home exterior with professional lighting installation Carrum"
              className="w-full h-full object-cover opacity-0"
            />
          </div>
          
          {/* Experience Badge - positioned over the image */}
          <div className="absolute bottom-8 left-8 lg:left-16 bg-white rounded-xl p-4 shadow-lg z-10">
            <span className="text-3xl font-bold text-securelec-red">30+</span>
            <span className="block text-sm text-securelec-gray">Years Experience</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
